import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Button, Stack, TextField, IconButton, Paper} from 'views/components';
import {Edit as EditIcon, InfoOutlined as InfoIcon} from 'views/icons';
import {MarkdownViewer, MarkdownEditor, Translate, translate} from 'views/module';

const GeneralInformation = (props) => {
  const {generalInformation, onSave} = props;
  const [editing, setEditing] = React.useState(false);
  const [clientName, setClientName] = React.useState(generalInformation.clientName);
  const [address, setAddress] = React.useState(generalInformation.address );
  const [clientInfo, setClientInfo] = React.useState(generalInformation.clientInfo.blob);
  const [notes, setNotes] = React.useState(generalInformation.notes.blob );
  const [mouseOver, setMouseOver] = React.useState(false);

  React.useEffect(() => {
    if (editing) return;
    setClientName(generalInformation?.clientName || '');
    setAddress(generalInformation?.address || '');
    setClientInfo(generalInformation?.clientInfo?.blob || '');
    setNotes(generalInformation?.notes?.blob || '');
  }, [generalInformation, editing]);

  const handleSave = () => {
    onSave({
      generalInformation: {
        clientName,
        address,
        clientInfo: {
          blob: clientInfo,
          editorType: 'MARKDOWN',
        },
        notes: {
          blob: notes,
          editorType: 'MARKDOWN',
        },
      },
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setClientName(generalInformation?.clientName);
    setAddress(generalInformation?.address);
    setClientInfo(generalInformation?.clientInfo?.blob);
    setNotes(generalInformation?.notes?.blob);
    setEditing(false);
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
      onMouseOver={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
          justifyContent: 'space-between',
          borderRadius: '4px 4px 0 0',
          width: '100%',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <InfoIcon sx={{mr: 1}} />
          <Typography variant="h6">
            <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Title" />
          </Typography>
        </Box>
        <IconButton
          sx={{
            visibility: editing ? 'hidden' : 'visible',
            opacity: mouseOver ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onClick={() => setEditing(true)}
        >
          <EditIcon/>
        </IconButton>
      </Box>
      <Box sx={{p: 2}}>
        {editing ? (
          <>
            <TextField
              label={<Translate expression="client.ClientDetail.Careplan.GeneralInformation.ClientName" />}
              value={clientName}
              onChange={(e) => setClientName(e)}
              fullWidth
              sx={{mb: 2}}
            />
            <TextField
              label={<Translate expression="client.ClientDetail.Careplan.GeneralInformation.Address" />}
              value={address}
              onChange={(e) => setAddress(e)}
              fullWidth
              sx={{mb: 2}}
            />
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Information" />
            </Typography>
            <MarkdownEditor value={clientInfo} onChange={setClientInfo} />
            <Typography variant="subtitle1" sx={{mt: 2, mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Notes" />
            </Typography>
            <MarkdownEditor value={notes} onChange={setNotes} />
            <Stack direction="row" justifyContent="flex-end" spacing={1} pt={1}>
              <Button onClick={handleCancel}>
                <Translate expression="generic.Cancel" />
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{minWidth: '100px'}}
              >
                <Translate expression="generic.Save" />
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.ClientName"/>:
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              {clientName || translate('client.ClientDetail.Careplan.GeneralInformation.NoClientname')}
            </Typography>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Address"/>:
            </Typography>
            <Typography variant="body2" sx={{mb: 2}}>
              {address || translate('client.ClientDetail.Careplan.GeneralInformation.NoAddress')}
            </Typography>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Information"/>
            </Typography>
            <MarkdownViewer
              value={clientInfo || translate('client.ClientDetail.Careplan.GeneralInformation.NoClientInformation')}
              truncate={true}
            />
            <Typography variant="subtitle1" sx={{mt: 2, mb: 1, fontWeight: 'bold'}}>
              <Translate expression="client.ClientDetail.Careplan.GeneralInformation.Notes"/>
            </Typography>
            <MarkdownViewer
              value={notes || translate('client.ClientDetail.Careplan.GeneralInformation.NoNotes')}
              truncate={true}
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

GeneralInformation.propTypes = {
  generalInformation: PropTypes.shape({
    clientName: PropTypes.string,
    address: PropTypes.string,
    clientInfo: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
    notes: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default GeneralInformation;
